import { ChevronLeft } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { VerificationCodeField } from '../../molecules/VerificationCodeField';

export interface EmailVerificationFormBodyProps {
  /** Callback when the form is submitted with the verification code */
  onSubmit?: (code: string) => void;
  /** Callback when resend code link is clicked */
  onResendCode?: () => void;
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Whether the form is in loading state */
  loading?: boolean;
  /** Error message to display (also triggers error styling on inputs) */
  error?: string;
  /** Title text (default: "Email Verification") */
  title?: string;
  /** Description text */
  description?: string;
  /** Number of digits in the code (default: 4) */
  codeLength?: number;
  /** Submit button text (default: "Submit") */
  submitButtonText?: string;
  /** Resend code link text (default: "Resend code") */
  resendCodeText?: string;
  /** Back button text (default: "Back") */
  backButtonText?: string;
  /** Accessible group label for the code field (default: none → unnamed group) */
  fieldAriaLabel?: string;
  /**
   * Horizontal alignment of the submit button / resend link within the form
   * column. `'start'` (default) keeps the dashboard login form left-aligned
   * (byte-identical to the pre-split `EmailVerificationForm`); `'center'` is
   * used by the onboarding branded card so Continue sits under the centered
   * digit boxes.
   */
  align?: 'start' | 'center';
  /** Optional `data-testid` for the code field group (e2e locator). */
  codeFieldTestId?: string;
  /** Optional `data-testid` for the submit button (e2e locator). */
  submitTestId?: string;
  /** Optional `data-testid` for the error message (e2e locator). */
  errorTestId?: string;
}

/**
 * EmailVerificationFormBody Organism
 *
 * The card-less content of the verification form: header (title + description),
 * boxed code field, submit button, resend link, and an optional back button.
 * Owns the `code`/`hasError` state that drives the field.
 *
 * Returns a React **Fragment** (top block + optional back button as siblings),
 * not a wrapping element, so a parent flex container's `justify-between` can
 * spread the header block and the back button — this is what keeps the dashboard
 * `EmailVerificationForm` (which composes `LoginCard` + this body) byte-identical.
 *
 * Used directly by onboarding inside its branded card, and indirectly by the
 * dashboard via `EmailVerificationForm`.
 */
export function EmailVerificationFormBody({
  onSubmit,
  onResendCode,
  onBack,
  loading = false,
  error,
  title = 'Email Verification',
  description = 'Enter the 4-digit verification code that has been sent to your email',
  codeLength = 4,
  submitButtonText = 'Submit',
  resendCodeText = 'Resend code',
  backButtonText = 'Back',
  fieldAriaLabel,
  align = 'start',
  codeFieldTestId,
  submitTestId,
  errorTestId,
}: EmailVerificationFormBodyProps) {
  const [code, setCode] = React.useState<string[]>(Array(codeLength).fill(''));
  const [hasError, setHasError] = React.useState(false);

  const handleSubmit = React.useCallback(() => {
    const fullCode = code.join('');
    if (fullCode.length !== codeLength) {
      setHasError(true);
      return;
    }
    setHasError(false);
    onSubmit?.(fullCode);
  }, [code, codeLength, onSubmit]);

  const handleCodeChange = React.useCallback(
    (newCode: string[]) => {
      setCode(newCode);
      if (hasError) setHasError(false);
    },
    [hasError]
  );

  const displayError = !!error || hasError;

  return (
    <>
      {/* Top section: Header + Form */}
      <div className="flex flex-col gap-5 items-center w-full">
        {/* Header section: Title + Description */}
        <div className="flex flex-col gap-5 items-center">
          {/* Title */}
          <p className="font-outfit font-medium text-[16px] leading-[1.2] text-dawn-black capitalize">
            {title}
          </p>

          {/* Description */}
          <p className="font-outfit font-normal text-[13px] leading-[1.3] text-dawn-black text-center max-w-[204px]">
            {description}
          </p>
        </div>

        {/* Form section: Code field + Submit button + Resend link */}
        <div
          className={cn(
            'flex flex-col gap-5 w-full',
            align === 'center' ? 'items-center' : 'items-start'
          )}
        >
          {/* Verification code field with error message */}
          <div className="flex flex-col gap-2 items-center w-full">
            <VerificationCodeField
              value={code}
              onChange={handleCodeChange}
              length={codeLength}
              error={displayError}
              disabled={loading}
              autoFocus
              onEnter={handleSubmit}
              ariaLabel={fieldAriaLabel}
              testId={codeFieldTestId}
            />
            {displayError && (
              <p
                data-testid={errorTestId}
                className="font-outfit text-[10px] font-normal capitalize leading-[1.2] text-error"
              >
                {error || 'Please enter the verification code'}
              </p>
            )}
          </div>

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            data-testid={submitTestId}
            className={cn(
              'w-[322px] h-10 bg-dawn-orange rounded flex items-center justify-center',
              'hover:bg-dawn-orange-hover transition-colors duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {loading ? (
              <span className="animate-spin w-[18px] h-[18px] border-2 border-dawn-white border-t-transparent rounded-full" />
            ) : (
              <span className="font-outfit font-medium text-[14px] leading-[1.2] text-dawn-white capitalize">
                {submitButtonText}
              </span>
            )}
          </button>

          {/* Resend code link */}
          {onResendCode && (
            <button
              onClick={onResendCode}
              disabled={loading}
              className={cn(
                'font-outfit font-medium text-[13px] leading-[1.3] text-grey-400',
                'hover:text-dawn-black transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {resendCodeText}
            </button>
          )}
        </div>
      </div>

      {/* Back button at bottom */}
      {onBack && (
        <button
          onClick={onBack}
          disabled={loading}
          className={cn(
            'flex items-center gap-0.5 text-grey-400',
            'hover:text-dawn-black transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <ChevronLeft className="w-3 h-3" />
          <span className="font-outfit font-medium text-[13px] leading-[1.3] text-center">
            {backButtonText}
          </span>
        </button>
      )}
    </>
  );
}

EmailVerificationFormBody.displayName = 'EmailVerificationFormBody';

export default EmailVerificationFormBody;
